import { useEffect, useRef } from "react";
import {
  useAnimate,
  useInView,
  useScroll,
  useTransform,
} from "motion/react";
import { ResultOf, readFragment } from "gql.tada";
import { TaggedEntryHeroById } from "@queries/entries/TaggedEntryHeroById";
import { TaggedEntryHeroFragment } from "@queries/fragments/TaggedEntryHeroFragment";
import { useTheme } from "@hooks/useTheme";
import { usePaletteValues } from "@hooks/usePaletteValues";
import { animationCurves } from "@utils/animationCurves";
import { MediaItem } from "@components/shared/MediaItem";
import { ScrollCueButton } from "@components/shared/ScrollCueButton";

export const TaggedEntryHero = ({
  entry: { taggedEntryHero: entry },
}: {
  entry: ResultOf<typeof TaggedEntryHeroById>;
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const [, animate] = useAnimate();

  // Scroll-linked background transition — same pattern as FullImageWithCopyHero.
  // Drives the root ThemeContextProvider's backgroundColor MotionValue between
  // the current palette's `deep` shade (while the hero is on-screen) and the
  // theme's configured `background` (after scrolling past the hero).
  const { theme, backgroundColor } = useTheme();
  const paletteValues = usePaletteValues(theme.palette);
  const inView = useInView(sectionRef);
  const { scrollYProgress: sectionExitProgress } = useScroll({
    target: sectionRef,
    offset: ["end end", "end start"],
  });
  const backgroundTransitionBackToDefault = useTransform(
    sectionExitProgress,
    [0, 1],
    [paletteValues.deep(), paletteValues[theme.background]()]
  );
  useTransform(() => {
    const scroll = sectionExitProgress.get();
    if (scroll === 0 || scroll === 1) return;
    backgroundColor.set(backgroundTransitionBackToDefault.get());
  });
  useEffect(() => {
    backgroundColor.animation?.cancel();
    animate(
      backgroundColor,
      inView
        ? backgroundTransitionBackToDefault.get()
        : paletteValues[theme.background](),
      animationCurves.default
    );
  }, [
    animate,
    theme,
    backgroundColor,
    paletteValues,
    backgroundTransitionBackToDefault,
    inView,
  ]);

  const hero = entry ? readFragment(TaggedEntryHeroFragment, entry) : null;
  if (!hero) return null;

  const bgMedia = hero.backgroundVideo?.url
    ? hero.backgroundVideo
    : hero.backgroundImage;

  const tags = (hero.tagsCollection?.items ?? []).filter(
    (t): t is NonNullable<typeof t> => t !== null
  );

  return (
    <section ref={sectionRef} className="relative h-screen overflow-hidden">
      {bgMedia && (
        <MediaItem
          item={bgMedia}
          alt=""
          aria-hidden
          className="absolute inset-0 size-full object-cover"
        />
      )}
      <div aria-hidden className="absolute inset-0 bg-neutral-black/50" />

      {/* Tags + headline — bottom, capped at 1327px */}
      <div className="absolute bottom-6 dsk:bottom-[54px] inset-x-0 z-10 px-6 dsk:px-[54px]">
        <div className="max-w-[1327px] mx-auto flex flex-col gap-7">
        {tags.length > 0 && (
          <ul className="flex flex-wrap gap-[10px]">
            {tags.map((tag) => (
              <li
                key={tag.sys.id}
                className="h-10 px-3 py-[11px] rounded-lg bg-neutral-cream/15 backdrop-blur-[25px] flex items-center"
              >
                <span className="text-neutral-cream text-body-regular tracking-[-0.14px] whitespace-nowrap">
                  {tag.tagLabel}
                </span>
              </li>
            ))}
          </ul>
        )}
        {hero.headline && (
          <h1 className="font-['Netflix_Sans_Title'] text-palette-current-highlight font-medium text-[60px] dsk:text-[125px] leading-[0.84] tracking-[-0.03em]">
            {hero.headline}
          </h1>
        )}
        </div>
      </div>
      <ScrollCueButton />
    </section>
  );
};
